import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'

import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { TwilioConversationsContext } from '../../providers'
import { useGetCustomerDetailQuery } from '../../redux/api'
import { CustomerEntry } from '../../sdk'
import { formatName } from '../../utils/formatCustomerName/formatCustomerName'
import { SmsParticipantBindings } from '../ChatServices/CustomerChatPanel'
import { useChat } from '../ChatServices/useChat'

interface Props {
  customer: CustomerEntry
  text: string | undefined
  handleTextChange: (number: string | undefined) => void
  handleSend: () => void
  handleCancel: () => void
  hasPreviousDialog: boolean
}

export const TextFieldForLink: FC<Props> = ({
  customer,
  text,
  handleTextChange,
  handleSend,
  handleCancel,
  hasPreviousDialog
}) => {
  const { accessToken, shopId } = useAccessTokenAndShopId()
  const { connectionStatus } = useContext(TwilioConversationsContext)

  const triggeredInitialJoin = useRef<boolean>(false)

  const { data: fetchedCustomer } = useGetCustomerDetailQuery({
    authorization: accessToken,
    shop: shopId,
    customer: [customer.id!]
  })

  const customerToDisplay = useMemo(() => {
    if (fetchedCustomer) {
      return fetchedCustomer[0]
    }
    return customer
  }, [customer, fetchedCustomer])

  useEffect(() => {
    if (triggeredInitialJoin.current === true) {
      triggeredInitialJoin.current = false
    }
  }, [triggeredInitialJoin, customerToDisplay])

  const customerId = customerToDisplay.id!
  const customerName = formatName(customer.information)

  const {
    joinConversation,
    activeConversation,
    activeConversationParticipants,
    errorMessage: sdkErrorMessage,
    isLoading
  } = useChat(customerId, customerName)

  useEffect(() => {
    if (!triggeredInitialJoin.current) {
      triggeredInitialJoin.current = true
      joinConversation(customerId, false, false)
    }
  }, [joinConversation, customerId, triggeredInitialJoin, customerToDisplay])

  const [showErrors, setShowErrors] = useState<boolean>(false)

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setShowErrors(true)
    }, 1000)
    return () => clearTimeout(timeoutId)
  }, [])

  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    if (isLoading) {
      setErrorMessage('')
      return
    }
    if (sdkErrorMessage !== '') {
      setErrorMessage(sdkErrorMessage)
      return
    }
    if (
      errorMessage === '' &&
      (connectionStatus !== 'connected' || !activeConversation)
    ) {
      setErrorMessage('Disconnected')
      return
    }
    if (
      errorMessage !== '' &&
      connectionStatus === 'connected' &&
      activeConversation
    ) {
      setErrorMessage('')
    }
  }, [
    activeConversation,
    errorMessage,
    sdkErrorMessage,
    connectionStatus,
    customerToDisplay,
    isLoading
  ])

  const activeConversationParticipantsToDisplay = useMemo(() => {
    const participant = activeConversationParticipants.find(
      (participant) => participant.type === 'sms'
    )
    if (!participant) {
      return ''
    }
    if (errorMessage) {
      return ''
    }
    const smsBindings = participant.bindings as SmsParticipantBindings
    return smsBindings.sms.address
  }, [activeConversationParticipants, errorMessage])

  const submitMessage = useCallback(() => {
    if (!activeConversation || connectionStatus !== 'connected' || !text) {
      return
    }
    activeConversation.sendMessage(text)
    handleTextChange('')

    handleSend()
  }, [activeConversation, connectionStatus, handleSend, handleTextChange, text])

  return (
    <>
      <DialogContent sx={{ pt: 2, pb: 0 }}>
        {showErrors && (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {errorMessage !== '' && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {errorMessage}
              </Alert>
            )}

            {!activeConversationParticipantsToDisplay &&
              errorMessage === '' &&
              !isLoading && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  No Phone numbers in conversation! Add a phone number using the
                  dropdown above!
                </Alert>
              )}
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography pl={1}>SMS Message</Typography>

          <TextField
            fullWidth
            multiline
            placeholder="Type your message here..."
            type="text"
            onChange={(event) => handleTextChange(event.target.value)}
            value={text}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ pt: 2 }}>
        <Button onClick={handleCancel} variant={'text'} sx={{ mr: 1 }}>
          {hasPreviousDialog ? 'Back' : 'Cancel'}
        </Button>

        {activeConversation &&
        connectionStatus === 'connected' &&
        activeConversationParticipantsToDisplay ? (
          <Tooltip title={text === '' ? 'Type a message first!' : ''}>
            <span>
              <Button
                onClick={submitMessage}
                disabled={text === ''}
                variant={'contained'}>
                Send SMS
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Tooltip
            title={activeConversationParticipantsToDisplay || 'Loading...'}>
            <span>
              <Button disabled={true} variant={'contained'}>
                Send SMS
              </Button>
            </span>
          </Tooltip>
        )}
      </DialogActions>
    </>
  )
}
